import { useEffect, useState } from "react";
import { RotateCw, Sparkles, Plus } from "lucide-react";
import api from "@/lib/api";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-US", {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
}) : "—");

const STATUS_PILL = {
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  expired: "bg-neutral-100 text-neutral-700 border-neutral-200",
};

export default function AdminFounding1Tab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", code: "FOUNDING1", mark_paid: true });
  const [addBusy, setAddBusy] = useState(false);
  const [addMsg, setAddMsg] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/promo/founding1");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load FOUNDING1 stats.");
    }
  };
  useEffect(() => { load(); }, []);

  const submitAdd = async () => {
    setAddBusy(true); setAddMsg("");
    try {
      await api.post("/admin/promo/founding1/manual-add", addForm);
      setAddMsg("Added.");
      setAddForm({ email: "", code: "FOUNDING1", mark_paid: true });
      setShowAdd(false);
      await load();
    } catch (e) { setAddMsg(e.response?.data?.detail || "Add failed"); }
    finally { setAddBusy(false); setTimeout(() => setAddMsg(""), 4000); }
  };

  if (err) return <p className="text-sm text-rose-700" data-testid="founding1-tab-err">{err}</p>;
  if (!data) return <p className="text-sm text-[var(--ff-text-muted)]">Loading…</p>;

  const pct = data.cap ? Math.min(100, Math.round((data.used / data.cap) * 100)) : 0;

  return (
    <div data-testid="admin-founding1-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-[var(--ff-citrus)] flex items-center gap-2">
            <Sparkles size={14} /> Promo · FOUNDING1
          </p>
          <h2 className="font-display text-2xl mt-1 text-[var(--ff-text)]">
            Founding member deposits
          </h2>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">
            Deposit: ${data.deposit_usd?.toFixed(2)} · Plan lock: {data.locked_plan_id} · First real charge: {data.launch_date}
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid="founding1-refresh">
          <RotateCw size={14} /> Refresh
        </button>
        <button onClick={() => setShowAdd(v => !v)} className="btn-primary text-sm" data-testid="founding1-add-toggle">
          <Plus size={14} /> Add manually
        </button>
      </div>

      {showAdd && (
        <div className="card-flat p-4 mt-4 grid sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-center" data-testid="founding1-add-form">
          <input type="email" placeholder="member@email.com" className="input-base" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} data-testid="founding1-add-email" />
          <select className="input-base" value={addForm.code} onChange={e => setAddForm({ ...addForm, code: e.target.value })} data-testid="founding1-add-code">
            {Object.keys(data.per_code || { FOUNDING1: 1 }).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={addForm.mark_paid} onChange={e => setAddForm({ ...addForm, mark_paid: e.target.checked })} data-testid="founding1-add-paid" /> Mark paid</label>
          <button onClick={submitAdd} disabled={addBusy || !addForm.email} className="btn-primary text-sm" data-testid="founding1-add-submit">{addBusy ? "Adding…" : "Add seat"}</button>
        </div>
      )}
      {addMsg && <p className="mt-2 text-sm text-[var(--ff-primary)]" data-testid="founding1-add-msg">{addMsg}</p>}

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="card-flat p-5" data-testid="founding1-total-used">
          <p className="overline text-[var(--ff-secondary)]">Redemptions used</p>
          <p className="mt-2 font-display text-4xl text-[var(--ff-text)]">{data.used}</p>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">
            {data.by_status.paid} paid · {data.by_status.pending} pending
          </p>
        </div>
        <div className="card-flat p-5" data-testid="founding1-remaining">
          <p className="overline text-emerald-700">Spots remaining</p>
          <p className="mt-2 font-display text-4xl text-[var(--ff-text)]">{data.remaining}</p>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">out of {data.cap} total</p>
        </div>
        <div className="card-flat p-5" data-testid="founding1-progress">
          <p className="overline text-[var(--ff-primary)]">Progress</p>
          <p className="mt-2 font-display text-4xl text-[var(--ff-text)]">{pct}%</p>
          <div className="mt-2 h-1.5 w-full bg-[var(--ff-muted)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--ff-citrus)] rounded-full" style={{ width: `${Math.max(pct, 2)}%` }} />
          </div>
          {data.is_full && (
            <p className="mt-2 text-xs font-semibold text-rose-800">Code is now invalid — cap reached.</p>
          )}
        </div>
      </div>

      {/* Per-code breakdown */}
      {data.by_code && (
        <div className="mt-8" data-testid="founding-by-code">
          <h3 className="font-display text-xl text-[var(--ff-text)]">Breakdown by code</h3>
          <div className="mt-4 card-flat overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--ff-muted)] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Code</th>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Ambassador</th>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Paid</th>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Pending</th>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Commission owed*</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ff-border)]">
                {data.by_code.map((row) => (
                  <tr key={row.code} data-testid={`founding-code-row-${row.code}`}>
                    <td className="px-4 py-3 font-mono font-semibold">{row.code}</td>
                    <td className="px-4 py-3">
                      {row.ambassador_ref_code ? (
                        <span>{row.label} <span className="text-[var(--ff-text-muted)] text-xs">({row.ambassador_ref_code})</span></span>
                      ) : <span className="text-[var(--ff-text-muted)]">Public / no attribution</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">{row.paid || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[var(--ff-text-muted)]">{row.pending || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.total || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">
                      ${(row.commission_owed_usd || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-[var(--ff-text-muted)] mt-2">
            * 10% of the $5 deposit per paid redemption. Launch-day annual + lifetime order commissions are tracked separately in the Ambassador Payouts tab.
          </p>
        </div>
      )}

      <div className="mt-8">
        <h3 className="font-display text-xl text-[var(--ff-text)]">Redemption log</h3>
        <div className="mt-4 card-flat overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="founding1-table">
            <thead className="bg-[var(--ff-muted)] text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Code</th>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Email</th>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Redeemed at</th>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Deposit</th>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Status</th>
                <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">First real charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ff-border)]">
              {data.redemptions.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ff-text-muted)]">No redemptions yet.</td></tr>
              )}
              {data.redemptions.map((r) => (
                <tr key={r.id} data-testid={`founding1-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--ff-text-secondary)]">{r.code}</td>
                  <td className="px-4 py-3 text-[var(--ff-text)]">{r.email}</td>
                  <td className="px-4 py-3 text-[var(--ff-text-secondary)]">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-right font-medium text-[var(--ff-text)]">${(r.deposit_usd || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs ${STATUS_PILL[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--ff-text-secondary)]">{r.launch_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
